import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline, Snackbar, Alert, GlobalStyles } from '@mui/material';
import { getCookie } from './utils/cookies';
import { silentRefresh, forceLogout } from './api/axiosConfig';
import { createAppTheme } from './theme/theme';
import { updateBrandingDom } from './utils/brandConfig';
import { applyDynamicBranding } from './applyTheme';

// Centralized routing
import AppRoutes from './routes/AppRoutes';

// Proactive refresh interval: 20 minutes (in milliseconds)
const REFRESH_INTERVAL_MS = 20 * 60 * 1000;
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

function App() {
    const dispatch = useDispatch();
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const refreshIntervalRef = useRef(null);

    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const tenantId = useSelector((state) => state.auth.user?.tenantId);
    const branding = useSelector((state) => state.branding);

    // Dynamically derive theme from centralized dynamic branding
    const theme = useMemo(() => createAppTheme(branding), [branding]);

    // Apply CSS variables and DOM metadata whenever branding changes
    useEffect(() => {
        applyDynamicBranding(branding);
        updateBrandingDom(branding);
    }, [branding]);

    // Cookie-based check combined with Redux state for reactivity
    const hasToken = isAuthenticated || !!getCookie('adminToken');

    // Load public branding before login, and tenant branding after login
    useEffect(() => {
        if (hasToken && tenantId) {
            dispatch.branding.loadAuthenticatedBrandingAsync(tenantId);
        } else {
            dispatch.branding.loadPublicBrandingAsync();
        }
    }, [dispatch, hasToken, tenantId]);

    useEffect(() => {
        const handleToast = (e) => {
            setToast({
                open: true,
                message: e.detail.message,
                severity: e.detail.severity || 'info'
            });
        };
        window.addEventListener('app-toast', handleToast);
        return () => window.removeEventListener('app-toast', handleToast);
    }, []);

    useEffect(() => {
        if (hasToken) {
            dispatch.auth.fetchMeAsync();
        }
    }, [dispatch, hasToken]);

    // ─── Frontend Inactivity Tracker ────────────────────────────────────────
    useEffect(() => {
        if (!hasToken) return;

        const updateActivity = () => {
            localStorage.setItem('lastActivityTime', Date.now().toString());
        };

        // Initialize on mount
        updateActivity();

        window.addEventListener('mousemove', updateActivity);
        window.addEventListener('keydown', updateActivity);
        window.addEventListener('click', updateActivity);
        window.addEventListener('scroll', updateActivity);

        const inactivityInterval = setInterval(() => {
            const lastActivityStr = localStorage.getItem('lastActivityTime');
            const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : Date.now();
            
            if (Date.now() - lastActivity > INACTIVITY_LIMIT_MS) {
                clearInterval(inactivityInterval);
                forceLogout('You have been logged out due to 30 minutes of inactivity.');
            }
        }, 60 * 1000); // Check every minute

        return () => {
            window.removeEventListener('mousemove', updateActivity);
            window.removeEventListener('keydown', updateActivity);
            window.removeEventListener('click', updateActivity);
            window.removeEventListener('scroll', updateActivity);
            clearInterval(inactivityInterval);
        };
    }, [hasToken]);

    // ─── Proactive Background Token Refresh ─────────────────────────────────
    // Runs silentRefresh every 20 minutes to keep the refresh token rotated
    // before the backend's RTR window expires. Only runs when authenticated.
    const startRefreshInterval = useCallback(() => {
        // Clear any existing interval first
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
        }

        refreshIntervalRef.current = setInterval(async () => {
            const hasRefreshToken = !!getCookie('adminRefreshToken');
            if (!hasRefreshToken) {
                // User logged out or token cleared — stop the interval
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
                return;
            }

            const success = await silentRefresh();
            if (!success) {
                console.warn('[App] Proactive token refresh failed. Will retry on next interval.');
            }
        }, REFRESH_INTERVAL_MS);
    }, []);

    useEffect(() => {
        if (hasToken) {
            startRefreshInterval();
        }

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [hasToken, tenantId, startRefreshInterval]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles
                styles={{
                    'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                    },
                    'input[type=number]': {
                        MozAppearance: 'textfield',
                    },
                    ':is(.MuiTableContainer-root, .MuiPaper-root, .MuiCard-root, .MuiBox-root):has(tbody tr:only-child td[colspan]) .MuiTablePagination-root': {
                        display: 'none !important',
                    },
                    ':is(.MuiTableContainer-root, .MuiPaper-root, .MuiCard-root, .MuiBox-root):has(tbody:empty) .MuiTablePagination-root': {
                        display: 'none !important',
                    },
                }}
            />
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast(prev => ({ ...prev, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
}

export default App;
